import { FormEvent, useEffect, useMemo, useState } from "react"
import {
  ADMIN_SESSION_EXPIRED_EVENT,
  AdminCoupon,
  AdminCustomer,
  AdminOrder,
  AdminProduct,
  AdminSession,
  AdminSettings,
  AdminUser,
  DashboardSummary,
  ReturnRecord,
  clearSession,
  createAdminUser,
  createCoupon,
  deleteAdminUser,
  getCustomers,
  getAdminUsers,
  getCoupons,
  getOrders,
  getProducts,
  getReturns,
  getSettings,
  getStoredSession,
  getSummary,
  login,
  updateOrder,
  updateProduct,
  updateReturn,
  updateAdminUser,
  updateCoupon,
  updateSettings,
  uploadProductImage,
} from "./api"

type Screen =
  | "dashboard"
  | "orders"
  | "products"
  | "customers"
  | "returns"
  | "inventory"
  | "settings"
  | "admins"
  | "coupons"

type LoadState = {
  summary: DashboardSummary | null
  products: AdminProduct[]
  orders: AdminOrder[]
  customers: AdminCustomer[]
  returns: ReturnRecord[]
  refunds: ReturnRecord[]
  settings: AdminSettings | null
  adminUsers: AdminUser[]
  coupons: AdminCoupon[]
}

const initialState: LoadState = {
  summary: null,
  products: [],
  orders: [],
  customers: [],
  returns: [],
  refunds: [],
  settings: null,
  adminUsers: [],
  coupons: [],
}

const navItems: Array<{ id: Screen; label: string; icon: string }> = [
  { id: "dashboard", label: "Dashboard", icon: "▦" },
  { id: "orders", label: "Orders", icon: "▤" },
  { id: "products", label: "Products", icon: "◫" },
  { id: "inventory", label: "Inventory", icon: "▥" },
  { id: "customers", label: "Customers", icon: "◉" },
  { id: "returns", label: "Returns & refunds", icon: "↺" },
  { id: "settings", label: "Settings", icon: "⚙" },
  { id: "admins", label: "Admin users", icon: "◌" },
  { id: "coupons", label: "Coupons", icon: "%" },
]

export default function App() {
  const [session, setSession] = useState<AdminSession | null>(() =>
    getStoredSession()
  )
  const [screen, setScreen] = useState<Screen>("dashboard")
  const [data, setData] = useState<LoadState>(initialState)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loginNotice, setLoginNotice] = useState<string | null>(null)

  async function loadAll() {
    if (!session) return
    setLoading(true)
    setError(null)
    try {
      const [
        summary,
        products,
        orders,
        customers,
        returnsData,
        settingsData,
        adminUsers,
        coupons,
      ] =
        await Promise.all([
          getSummary(),
          getProducts(),
          getOrders(),
          getCustomers(),
          getReturns(),
          getSettings(),
          getAdminUsers(),
          getCoupons(),
        ])
      setData({
        summary,
        products: products.products,
        orders: orders.orders,
        customers: customers.customers,
        returns: returnsData.returns,
        refunds: returnsData.refunds,
        settings: settingsData.settings,
        adminUsers: adminUsers.admin_users,
        coupons: coupons.coupons,
      })
    } catch (err) {
      const message = messageFrom(err)
      if (message.toLowerCase().includes("sign in again")) {
        clearSession()
        setData(initialState)
        setLoginNotice(message)
        setSession(null)
      } else {
        setError(message)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const handleExpired = () => {
      setData(initialState)
      setError(null)
      setLoginNotice("Your admin session expired. Please sign in again.")
      setSession(null)
    }

    window.addEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleExpired)
    return () => {
      window.removeEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleExpired)
    }
  }, [])

  useEffect(() => {
    loadAll()
  }, [session])

  if (!session) {
    return (
      <LoginScreen
        notice={loginNotice}
        onLogin={(nextSession) => {
          setLoginNotice(null)
          setSession(nextSession)
        }}
      />
    )
  }

  return (
    <div className="admin-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">D</div>
          <div>
            <strong>DentixUAE</strong>
            <span>Commerce admin</span>
          </div>
        </div>

        <div className="admin-person">
          <div className="avatar">{initials(session.admin.name)}</div>
          <div>
            <strong>{session.admin.name}</strong>
            <span>{session.admin.role}</span>
          </div>
        </div>

        <nav>
          {navItems.map((item) => (
            <button
              key={item.id}
              className={screen === item.id ? "active" : ""}
              onClick={() => setScreen(item.id)}
            >
              <span>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        <button
          className="ghost-button logout"
          onClick={() => {
            clearSession()
            setLoginNotice(null)
            setSession(null)
          }}
        >
          Log out
        </button>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div>
            <span className="eyebrow">Cloudflare edge</span>
            <h1>{titleFor(screen)}</h1>
          </div>
          <div className="topbar-actions">
            <span className="health-dot" />
            <span className="muted">Connected to Worker API</span>
            <button className="secondary-button" onClick={loadAll}>
              Refresh
            </button>
          </div>
        </header>

        {error ? <div className="notice danger">{error}</div> : null}
        {loading ? <div className="notice">Loading latest store data...</div> : null}

        {screen === "dashboard" ? <Dashboard data={data} /> : null}
        {screen === "orders" ? (
          <OrdersScreen orders={data.orders} onChanged={loadAll} />
        ) : null}
        {screen === "products" ? (
          <ProductsScreen products={data.products} onChanged={loadAll} />
        ) : null}
        {screen === "inventory" ? (
          <InventoryScreen products={data.products} onChanged={loadAll} />
        ) : null}
        {screen === "customers" ? (
          <CustomersScreen customers={data.customers} orders={data.orders} />
        ) : null}
        {screen === "returns" ? (
          <ReturnsScreen
            returns={data.returns}
            refunds={data.refunds}
            onChanged={loadAll}
          />
        ) : null}
        {screen === "settings" ? (
          <SettingsScreen settings={data.settings} onChanged={loadAll} />
        ) : null}
        {screen === "admins" ? (
          <AdminUsersScreen users={data.adminUsers} onChanged={loadAll} />
        ) : null}
        {screen === "coupons" ? (
          <CouponsScreen coupons={data.coupons} onChanged={loadAll} />
        ) : null}
      </main>
    </div>
  )
}

function LoginScreen({
  notice,
  onLogin,
}: {
  notice?: string | null
  onLogin: (session: AdminSession) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const form = new FormData(event.currentTarget)
    try {
      const session = await login(
        String(form.get("email") || ""),
        String(form.get("password") || "")
      )
      onLogin(session)
    } catch (err) {
      setError(messageFrom(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="login-page">
      <form className="login-card" onSubmit={submit}>
        <div className="brand login-brand">
          <div className="brand-mark">D</div>
          <div>
            <strong>DentixUAE</strong>
            <span>Cloudflare admin</span>
          </div>
        </div>
        <h1>Sign in</h1>
        <label>
          Email
          <input name="email" type="email" autoComplete="email" required />
        </label>
        <label>
          Password
          <input
            name="password"
            type="password"
            autoComplete="current-password"
            required
          />
        </label>
        {notice ? <div className="notice">{notice}</div> : null}
        {error ? <div className="notice danger">{error}</div> : null}
        <button className="primary-button" disabled={busy}>
          {busy ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </main>
  )
}

function Dashboard({ data }: { data: LoadState }) {
  const summary = data.summary
  const cards = [
    ["Revenue", formatMoney(summary?.revenue_aed || 0)],
    ["Orders", String(summary?.orders || 0)],
    ["Products", `${summary?.published_products || 0}/${summary?.products || 0}`],
    ["Customers", String(summary?.customers || 0)],
    ["Open returns", String(summary?.returns || 0)],
  ]

  return (
    <section className="stack">
      <div className="metric-grid">
        {cards.map(([label, value]) => (
          <article className="metric-card" key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </article>
        ))}
      </div>

      <div className="split-grid">
        <section className="panel">
          <PanelHeader title="Latest orders" subtitle="Live from Supabase edge tables" />
          <OrderTable orders={data.orders.slice(0, 6)} compact />
        </section>
        <section className="panel">
          <PanelHeader title="Low stock" subtitle="Products that need attention" />
          <div className="stock-list">
            {data.products
              .filter((product) => product.inventory_quantity <= 5)
              .slice(0, 8)
              .map((product) => (
                <div key={product.id} className="stock-row">
                  <img src={product.thumbnail || ""} alt="" />
                  <div>
                    <strong>{product.title}</strong>
                    <span>{product.sku || product.handle}</span>
                  </div>
                  <b>{product.inventory_quantity}</b>
                </div>
              ))}
            {!data.products.some((product) => product.inventory_quantity <= 5) ? (
              <p className="empty">No low-stock products right now.</p>
            ) : null}
          </div>
        </section>
      </div>
    </section>
  )
}

function OrdersScreen({
  orders,
  onChanged,
}: {
  orders: AdminOrder[]
  onChanged: () => Promise<void>
}) {
  const [selectedId, setSelectedId] = useState(orders[0]?.id || "")
  const selected = orders.find((order) => order.id === selectedId) || orders[0]

  useEffect(() => {
    if (!selectedId && orders[0]) setSelectedId(orders[0].id)
  }, [orders, selectedId])

  return (
    <section className="split-grid wide">
      <div className="panel">
        <PanelHeader
          title="Orders"
          subtitle="Customer checkout orders appear here instantly"
        />
        <OrderTable
          orders={orders}
          selectedId={selected?.id}
          onSelect={(order) => setSelectedId(order.id)}
        />
      </div>
      <OrderDetails order={selected} onChanged={onChanged} />
    </section>
  )
}

function OrderTable({
  orders,
  compact,
  selectedId,
  onSelect,
}: {
  orders: AdminOrder[]
  compact?: boolean
  selectedId?: string
  onSelect?: (order: AdminOrder) => void
}) {
  if (!orders.length) {
    return <p className="empty">No orders yet.</p>
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Order</th>
            <th>Customer</th>
            <th>Status</th>
            {!compact ? <th>Payment</th> : null}
            <th>Total</th>
            {!compact ? <th>Date</th> : null}
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr
              key={order.id}
              className={selectedId === order.id ? "selected" : ""}
              onClick={() => onSelect?.(order)}
            >
              <td>#{order.display_id || order.id.slice(-6)}</td>
              <td>{order.email || "Guest"}</td>
              <td>
                <StatusPill value={order.status} />
              </td>
              {!compact ? (
                <td>
                  <StatusPill value={order.payment_status} subtle />
                </td>
              ) : null}
              <td>{formatMoney(order.total)}</td>
              {!compact ? <td>{formatDate(order.created_at)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function OrderDetails({
  order,
  onChanged,
}: {
  order?: AdminOrder
  onChanged: () => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState("")
  const [draft, setDraft] = useState({
    status: "",
    payment_status: "",
    fulfillment_status: "",
    tracking_number: "",
  })

  useEffect(() => {
    setNote(order?.admin_note || "")
    setDraft({
      status: order?.status || "",
      payment_status: order?.payment_status || "",
      fulfillment_status: order?.fulfillment_status || "",
      tracking_number: order?.tracking_number || "",
    })
  }, [
    order?.id,
    order?.admin_note,
    order?.status,
    order?.payment_status,
    order?.fulfillment_status,
    order?.tracking_number,
  ])

  if (!order) {
    return (
      <aside className="panel details-panel">
        <p className="empty">Select an order to review it.</p>
      </aside>
    )
  }

  async function save(patch: Partial<AdminOrder>) {
    setBusy(true)
    try {
      await updateOrder(order!.id, patch)
      await onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <aside className="panel details-panel">
      <PanelHeader
        title={`Order #${order.display_id || order.id.slice(-6)}`}
        subtitle={`${order.email || "Guest"} • ${formatDate(order.created_at)}`}
      />

      <div className="status-grid">
        <label>
          Order status
          <select
            value={draft.status}
            onChange={(event) =>
              setDraft({ ...draft, status: event.target.value })
            }
          >
            {["pending", "processing", "completed", "refunded", "canceled", "failed"].map(
              (status) => (
                <option key={status}>{status}</option>
              )
            )}
          </select>
        </label>
        <label>
          Payment
          <select
            value={draft.payment_status}
            onChange={(event) =>
              setDraft({ ...draft, payment_status: event.target.value })
            }
          >
            {["awaiting", "authorized", "captured", "refunded", "failed"].map((status) => (
              <option key={status}>{status}</option>
            ))}
          </select>
        </label>
        <label>
          Fulfillment
          <select
            value={draft.fulfillment_status}
            onChange={(event) =>
              setDraft({ ...draft, fulfillment_status: event.target.value })
            }
          >
            {["not_fulfilled", "partially_fulfilled", "fulfilled", "returned"].map(
              (status) => (
                <option key={status}>{status}</option>
              )
            )}
          </select>
        </label>
        <label>
          Tracking number
          <input
            value={draft.tracking_number}
            onChange={(event) =>
              setDraft({ ...draft, tracking_number: event.target.value })
            }
          />
        </label>
      </div>
      <button
        className="primary-button"
        disabled={busy}
        onClick={() => save(draft)}
      >
        {busy ? "Saving..." : "Save order status"}
      </button>

      <div className="order-lines">
        {order.items.map((item) => {
          const metadata = (item.metadata || {}) as Record<string, unknown>
          const designUrl = stringValue(metadata.custom_design_file_url)
          const designName =
            stringValue(metadata.custom_design_file_name) || "Uploaded design"
          const designNote = stringValue(metadata.custom_design_note)

          return (
            <div className="order-line-wrap" key={item.id}>
              <div className="order-line">
                <img src={item.thumbnail || ""} alt="" />
                <div>
                  <strong>{item.title}</strong>
                  <span>
                    {item.quantity} × {formatMoney(item.unit_price)}
                  </span>
                </div>
                <b>{formatMoney(item.total)}</b>
              </div>
              {(designUrl || designNote) && (
                <div className="order-line-customization">
                  <strong>Customization</strong>
                  {designNote ? <p>{designNote}</p> : null}
                  {designUrl ? (
                    <a href={designUrl} target="_blank" rel="noreferrer">
                      Open {designName}
                    </a>
                  ) : null}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="totals">
        <span>Subtotal</span>
        <strong>{formatMoney(order.subtotal)}</strong>
        <span>Delivery</span>
        <strong>{formatMoney(order.shipping_total)}</strong>
        {order.discount_total ? (
          <>
            <span>Discount</span>
            <strong>-{formatMoney(order.discount_total)}</strong>
          </>
        ) : null}
        <span>Total</span>
        <strong>{formatMoney(order.total)}</strong>
      </div>

      <div className="address-grid">
        <AddressCard title="Shipping" address={order.shipping_address} />
        <AddressCard title="Billing" address={order.billing_address} />
      </div>

      <label>
        Admin note
        <textarea value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
      <button
        className="primary-button"
        disabled={busy}
        onClick={() => save({ admin_note: note })}
      >
        Save order notes
      </button>
    </aside>
  )
}

function ProductsScreen({
  products,
  onChanged,
}: {
  products: AdminProduct[]
  onChanged: () => Promise<void>
}) {
  const [query, setQuery] = useState("")
  const [selectedId, setSelectedId] = useState(products[0]?.id || "")
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return products
    return products.filter((product) =>
      [product.title, product.sku, product.handle]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    )
  }, [products, query])
  const selected = products.find((product) => product.id === selectedId) || filtered[0]

  useEffect(() => {
    if (!selectedId && products[0]) setSelectedId(products[0].id)
  }, [products, selectedId])

  return (
    <section className="split-grid wide">
      <div className="panel">
        <PanelHeader title="Products" subtitle="Edit price, stock, images, SEO and Google fields" />
        <input
          className="search"
          placeholder="Search products"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="product-list">
          {filtered.map((product) => (
            <button
              key={product.id}
              className={selected?.id === product.id ? "product-row active" : "product-row"}
              onClick={() => setSelectedId(product.id)}
            >
              <img src={product.thumbnail || ""} alt="" />
              <span>
                <strong>{product.title}</strong>
                <small>{product.sku || product.handle}</small>
              </span>
              <b>{formatMoney(product.price_aed)}</b>
              <StatusPill value={product.status} />
            </button>
          ))}
        </div>
      </div>
      <ProductEditor product={selected} onChanged={onChanged} />
    </section>
  )
}

function ProductEditor({
  product,
  onChanged,
}: {
  product?: AdminProduct
  onChanged: () => Promise<void>
}) {
  const [draft, setDraft] = useState<AdminProduct | null>(product || null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setDraft(product || null)
  }, [product?.id])

  if (!draft) {
    return (
      <aside className="panel details-panel">
        <p className="empty">Select a product to edit it.</p>
      </aside>
    )
  }

  const variant = draft.variants[0]
  const seoAudit = getProductSeoAudit(draft)
  const metadata = (draft.metadata || {}) as Record<string, unknown>
  const customizationEnabled =
    metadata.customization_enabled === true ||
    metadata.custom_design_enabled === true
  const customizationUploadRequired =
    customizationEnabled && metadata.customization_upload_required !== false
  const customizationNoteRequired =
    customizationEnabled && metadata.customization_note_required !== false

  function updateMetadata(patch: Record<string, unknown>) {
    setDraft((current) =>
      current
        ? {
            ...current,
            metadata: {
              ...(current.metadata || {}),
              ...patch,
            },
          }
        : current
    )
  }

  async function save() {
    if (!draft) return
    setBusy(true)
    try {
      await updateProduct(draft.id, {
        title: draft.title,
        handle: draft.handle,
        status: draft.status,
        sku: draft.sku,
        description: draft.description,
        short_description: draft.short_description,
        thumbnail: draft.thumbnail,
        images: draft.images,
        seo_title: draft.seo_title,
        seo_description: draft.seo_description,
        google_product_category: draft.google_product_category,
        brand: draft.brand,
        metadata: draft.metadata || {},
        variant: variant
          ? {
              id: variant.id,
              sku: variant.sku,
              price_aed: Number(variant.price_aed),
              compare_at_price_aed: variant.compare_at_price_aed
                ? Number(variant.compare_at_price_aed)
                : null,
              inventory_quantity: Number(variant.inventory_quantity),
              manage_inventory: Boolean(variant.manage_inventory),
              allow_backorder: Boolean(variant.allow_backorder),
            }
          : undefined,
      })
      await onChanged()
    } finally {
      setBusy(false)
    }
  }

  async function upload(file: File) {
    if (!draft) return
    const current = draft
    setBusy(true)
    try {
      const uploaded = await uploadProductImage(file)
      const images = [...(current.images || []), uploaded.url]
      setDraft({ ...current, images, thumbnail: current.thumbnail || uploaded.url })
    } finally {
      setBusy(false)
    }
  }

  function setImages(images: string[], thumbnail?: string | null) {
    setDraft((current) =>
      current
        ? {
            ...current,
            images,
            thumbnail:
              typeof thumbnail === "undefined"
                ? current.thumbnail || images[0] || null
                : thumbnail,
          }
        : current
    )
  }

  return (
    <aside className="panel details-panel product-editor">
      <PanelHeader title="Edit product" subtitle={draft.handle} />
      <div className="seo-score-card">
        <div>
          <strong>{seoAudit.score}% SEO</strong>
          <span>{seoAudit.status}</span>
        </div>
        <div className="seo-meter" aria-label={`SEO score ${seoAudit.score}%`}>
          <span style={{ width: `${seoAudit.score}%` }} />
        </div>
      </div>
      <div className="seo-checklist">
        {seoAudit.checks.map((check) => (
          <span key={check.label} className={check.ok ? "ok" : ""}>
            {check.ok ? "✓" : "•"} {check.label}
          </span>
        ))}
      </div>
      <div className="image-manager">
        <div className="image-manager-head">
          <div>
            <strong>Product images</strong>
            <span>Main image and gallery, like WooCommerce</span>
          </div>
          <label className="upload-box compact">
            Upload
            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) upload(file)
                event.currentTarget.value = ""
              }}
            />
          </label>
        </div>
        <div className="image-grid">
          {(draft.images?.length ? draft.images : draft.thumbnail ? [draft.thumbnail] : []).map(
            (image, index) => {
              const isPrimary = image === draft.thumbnail || (!draft.thumbnail && index === 0)
              return (
                <div key={`${image}-${index}`} className={isPrimary ? "image-tile primary" : "image-tile"}>
                  <img src={image} alt={`${draft.title} image ${index + 1}`} />
                  <span>{isPrimary ? "Main" : `Gallery ${index + 1}`}</span>
                  <div className="image-actions">
                    <button type="button" onClick={() => setDraft({ ...draft, thumbnail: image })}>
                      Set main
                    </button>
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => {
                        const images = [...(draft.images || [])]
                        ;[images[index - 1], images[index]] = [images[index], images[index - 1]]
                        setImages(images, draft.thumbnail)
                      }}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      disabled={index >= (draft.images || []).length - 1}
                      onClick={() => {
                        const images = [...(draft.images || [])]
                        ;[images[index + 1], images[index]] = [images[index], images[index + 1]]
                        setImages(images, draft.thumbnail)
                      }}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => {
                        const images = (draft.images || []).filter((_, itemIndex) => itemIndex !== index)
                        const thumbnail = draft.thumbnail === image ? images[0] || null : draft.thumbnail
                        setImages(images, thumbnail)
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )
            }
          )}
          {!draft.images?.length && !draft.thumbnail && (
            <div className="image-empty">No images yet. Upload a main product image.</div>
          )}
        </div>
      </div>
      <label>
        Product name
        <input
          value={draft.title}
          onChange={(event) => setDraft({ ...draft, title: event.target.value })}
        />
      </label>
      <label>
        Handle
        <input
          value={draft.handle}
          onChange={(event) => setDraft({ ...draft, handle: event.target.value })}
        />
      </label>
      <div className="status-grid">
        <label>
          Status
          <select
            value={draft.status}
            onChange={(event) =>
              setDraft({ ...draft, status: event.target.value as AdminProduct["status"] })
            }
          >
            <option>published</option>
            <option>draft</option>
            <option>archived</option>
          </select>
        </label>
        <label>
          SKU
          <input
            value={variant?.sku || draft.sku || ""}
            onChange={(event) => updateDraftVariant(draft, setDraft, { sku: event.target.value })}
          />
        </label>
        <label>
          Price AED
          <input
            type="number"
            step="0.01"
            value={variant?.price_aed ?? draft.price_aed}
            onChange={(event) =>
              updateDraftVariant(draft, setDraft, {
                price_aed: Number(event.target.value),
              })
            }
          />
        </label>
        <label>
          Compare price
          <input
            type="number"
            step="0.01"
            value={variant?.compare_at_price_aed || ""}
            onChange={(event) =>
              updateDraftVariant(draft, setDraft, {
                compare_at_price_aed: event.target.value
                  ? Number(event.target.value)
                  : null,
              })
            }
          />
        </label>
        <label>
          Stock
          <input
            type="number"
            value={variant?.inventory_quantity ?? draft.inventory_quantity}
            onChange={(event) =>
              updateDraftVariant(draft, setDraft, {
                inventory_quantity: Number(event.target.value),
              })
            }
          />
        </label>
      </div>
      <label>
        Short description
        <textarea
          value={draft.short_description || ""}
          onChange={(event) =>
            setDraft({ ...draft, short_description: event.target.value })
          }
        />
      </label>
      <label>
        Full description
        <textarea
          value={draft.description || ""}
          onChange={(event) => setDraft({ ...draft, description: event.target.value })}
        />
      </label>
      <div className="check-row">
        <label>
          <input
            type="checkbox"
            checked={Boolean(variant?.manage_inventory)}
            onChange={(event) =>
              updateDraftVariant(draft, setDraft, {
                manage_inventory: event.target.checked,
              })
            }
          />
          Track stock
        </label>
        <label>
          <input
            type="checkbox"
            checked={Boolean(variant?.allow_backorder)}
            onChange={(event) =>
              updateDraftVariant(draft, setDraft, {
                allow_backorder: event.target.checked,
              })
            }
          />
          Allow backorders
        </label>
      </div>
      <div className="customization-card">
        <div>
          <strong>Custom design upload</strong>
          <span>Show upload and customization note on this product page.</span>
        </div>
        <div className="check-row">
          <label>
            <input
              type="checkbox"
              checked={customizationEnabled}
              onChange={(event) =>
                updateMetadata({
                  customization_enabled: event.target.checked,
                  custom_design_enabled: event.target.checked,
                  customization_upload_required: event.target.checked,
                  customization_note_required: event.target.checked,
                })
              }
            />
            Enable customization
          </label>
          <label>
            <input
              type="checkbox"
              checked={customizationUploadRequired}
              disabled={!customizationEnabled}
              onChange={(event) =>
                updateMetadata({
                  customization_upload_required: event.target.checked,
                })
              }
            />
            Require design file
          </label>
          <label>
            <input
              type="checkbox"
              checked={customizationNoteRequired}
              disabled={!customizationEnabled}
              onChange={(event) =>
                updateMetadata({
                  customization_note_required: event.target.checked,
                })
              }
            />
            Require note
          </label>
        </div>
      </div>
      <label>
        SEO title
        <input
          value={draft.seo_title || ""}
          onChange={(event) => setDraft({ ...draft, seo_title: event.target.value })}
        />
      </label>
      <label>
        SEO description
        <textarea
          value={draft.seo_description || ""}
          onChange={(event) =>
            setDraft({ ...draft, seo_description: event.target.value })
          }
        />
      </label>
      <label>
        Google product category
        <input
          value={draft.google_product_category || ""}
          onChange={(event) =>
            setDraft({ ...draft, google_product_category: event.target.value })
          }
        />
      </label>
      <label>
        Brand
        <input
          value={draft.brand || ""}
          onChange={(event) => setDraft({ ...draft, brand: event.target.value })}
        />
      </label>
      <button className="primary-button" disabled={busy} onClick={save}>
        {busy ? "Saving..." : "Save product"}
      </button>
    </aside>
  )
}

function InventoryScreen({
  products,
  onChanged,
}: {
  products: AdminProduct[]
  onChanged: () => Promise<void>
}) {
  const [saving, setSaving] = useState<string | null>(null)

  async function save(product: AdminProduct, quantity: number) {
    const variant = product.variants[0]
    if (!variant) return
    setSaving(product.id)
    try {
      await updateProduct(product.id, {
        variant: { id: variant.id, inventory_quantity: quantity },
      })
      await onChanged()
    } finally {
      setSaving(null)
    }
  }

  return (
    <section className="panel">
      <PanelHeader title="Inventory" subtitle="Fast stock edits for products and variants" />
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>Stock</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {products.map((product) => {
              const variant = product.variants[0]
              return (
                <tr key={product.id}>
                  <td>{product.title}</td>
                  <td>{variant?.sku || product.sku || "-"}</td>
                  <td>
                    <input
                      className="stock-input"
                      type="number"
                      defaultValue={variant?.inventory_quantity ?? product.inventory_quantity}
                      onBlur={(event) => save(product, Number(event.target.value))}
                    />
                  </td>
                  <td>
                    <StatusPill value={stockLabel(product.inventory_quantity)} />
                  </td>
                  <td>{saving === product.id ? "Saving..." : ""}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function CustomersScreen({
  customers,
  orders,
}: {
  customers: AdminCustomer[]
  orders: AdminOrder[]
}) {
  return (
    <section className="panel">
      <PanelHeader
        title="Customers"
        subtitle="Accounts and imported WooCommerce customers by email"
      />
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Orders</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer) => (
              <tr key={customer.id}>
                <td>
                  {[customer.first_name, customer.last_name].filter(Boolean).join(" ") ||
                    "Customer"}
                </td>
                <td>{customer.email}</td>
                <td>{customer.phone || "-"}</td>
                <td>{orders.filter((order) => order.email === customer.email).length}</td>
                <td>{formatDate(customer.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function ReturnsScreen({
  returns,
  refunds,
  onChanged,
}: {
  returns: ReturnRecord[]
  refunds: ReturnRecord[]
  onChanged: () => Promise<void>
}) {
  const [busy, setBusy] = useState<string | null>(null)

  async function save(record: ReturnRecord, status: string) {
    setBusy(record.id)
    try {
      await updateReturn(record.id, { status })
      await onChanged()
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="split-grid">
      <div className="panel">
        <PanelHeader title="Returns" subtitle="Customer return requests" />
        <RecordList records={returns} busy={busy} onStatus={save} />
      </div>
      <div className="panel">
        <PanelHeader title="Refunds" subtitle="Refund records imported or created" />
        <RecordList records={refunds} busy={busy} onStatus={save} readonly />
      </div>
    </section>
  )
}

function RecordList({
  records,
  busy,
  readonly,
  onStatus,
}: {
  records: ReturnRecord[]
  busy: string | null
  readonly?: boolean
  onStatus: (record: ReturnRecord, status: string) => void
}) {
  if (!records.length) return <p className="empty">No records to show.</p>

  return (
    <div className="record-list">
      {records.map((record) => (
        <div className="record-row" key={record.id}>
          <div>
            <strong>{record.customer_email || record.order_id || record.id}</strong>
            <span>{record.reason || formatDate(record.created_at)}</span>
          </div>
          <b>{formatMoney(record.amount_aed || 0)}</b>
          {readonly ? (
            <StatusPill value={record.status} />
          ) : (
            <select
              value={record.status}
              disabled={busy === record.id}
              onChange={(event) => onStatus(record, event.target.value)}
            >
              {["requested", "approved", "received", "refunded", "rejected"].map(
                (status) => (
                  <option key={status}>{status}</option>
                )
              )}
            </select>
          )}
        </div>
      ))}
    </div>
  )
}

function SettingsScreen({
  settings,
  onChanged,
}: {
  settings: AdminSettings | null
  onChanged: () => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shippingDraft, setShippingDraft] = useState({
    free_shipping_threshold_aed:
      settings?.shipping.free_shipping_threshold_aed ?? 99,
    delivery_fee_aed: settings?.shipping.delivery_fee_aed ?? 18,
  })
  const [emailDraft, setEmailDraft] = useState({
    admin_order_emails_enabled:
      settings?.email.admin_order_emails_enabled ?? false,
    admin_order_email: settings?.email.admin_order_email ?? "",
    customer_order_emails_enabled:
      settings?.email.customer_order_emails_enabled ?? false,
  })

  useEffect(() => {
    if (!settings) return
    setShippingDraft({
      free_shipping_threshold_aed:
        settings.shipping.free_shipping_threshold_aed,
      delivery_fee_aed: settings.shipping.delivery_fee_aed,
    })
  }, [
    settings?.shipping.free_shipping_threshold_aed,
    settings?.shipping.delivery_fee_aed,
  ])

  useEffect(() => {
    if (!settings) return
    setEmailDraft({
      admin_order_emails_enabled: settings.email.admin_order_emails_enabled,
      admin_order_email: settings.email.admin_order_email ?? "",
      customer_order_emails_enabled: settings.email.customer_order_emails_enabled,
    })
  }, [
    settings?.email.admin_order_emails_enabled,
    settings?.email.admin_order_email,
    settings?.email.customer_order_emails_enabled,
  ])

  if (!settings) {
    return (
      <section className="panel">
        <p className="empty">Settings are not loaded yet.</p>
      </section>
    )
  }

  async function switchStripeMode(mode: "test" | "live") {
    setBusy(true)
    setError(null)
    try {
      await updateSettings({ payments: { stripe_mode: mode } } as Partial<AdminSettings>)
      await onChanged()
    } catch (err) {
      setError(messageFrom(err))
    } finally {
      setBusy(false)
    }
  }

  async function saveShipping(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await updateSettings({
        shipping: {
          free_shipping_threshold_aed: Number(
            shippingDraft.free_shipping_threshold_aed
          ),
          delivery_fee_aed: Number(shippingDraft.delivery_fee_aed),
        },
      } as Partial<AdminSettings>)
      await onChanged()
    } catch (err) {
      setError(messageFrom(err))
    } finally {
      setBusy(false)
    }
  }

  async function saveEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await updateSettings({
        email: {
          admin_order_emails_enabled: emailDraft.admin_order_emails_enabled,
          admin_order_email: emailDraft.admin_order_email.trim(),
          customer_order_emails_enabled:
            emailDraft.customer_order_emails_enabled,
        },
      } as Partial<AdminSettings>)
      await onChanged()
    } catch (err) {
      setError(messageFrom(err))
    } finally {
      setBusy(false)
    }
  }

  const rows = [
    ["Cash on delivery", settings.payments.cod_enabled],
    ["Stripe", settings.payments.stripe_enabled],
    ["Stripe test keys", settings.payments.stripe_test_configured],
    ["Stripe live keys", settings.payments.stripe_live_configured],
    ["Zoho ZeptoMail", settings.email.resend_configured ?? false],
    ["Order notifications", settings.email.order_notifications_enabled],
    ["Customer order emails", settings.email.customer_order_emails_enabled],
  ] as const

  return (
    <section className="split-grid">
      <div className="panel">
        <PanelHeader title="Payment & email" subtitle="Worker runtime configuration" />
        <div className="stripe-switch-card">
          <div>
            <strong>Stripe</strong>
            <StatusPill value={settings.payments.stripe_mode} />
          </div>
          <button
            className="primary-button"
            disabled={
              busy ||
              (settings.payments.stripe_mode === "test" &&
                !settings.payments.stripe_live_configured) ||
              (settings.payments.stripe_mode === "live" &&
                !settings.payments.stripe_test_configured)
            }
            onClick={() =>
              switchStripeMode(
                settings.payments.stripe_mode === "live" ? "test" : "live"
              )
            }
          >
            {busy
              ? "Switching..."
              : settings.payments.stripe_mode === "live"
                ? "Switch to Test"
                : "Switch to Live"}
          </button>
          {error ? <div className="notice danger">{error}</div> : null}
        </div>
        <div className="settings-list">
          <div className="settings-row">
            <span>Email sender</span>
            <strong>{settings.email.from_address || "DentixUAE <care@dentixuae.com>"}</strong>
          </div>
          {rows.map(([label, enabled]) => (
            <div className="settings-row" key={label}>
              <span>{label}</span>
              <StatusPill value={enabled ? "configured" : "missing"} />
            </div>
          ))}
        </div>
      </div>
      <form className="panel" onSubmit={saveEmail}>
        <PanelHeader title="Order emails" subtitle="Admin and customer notifications" />
        <div className="settings-form">
          <div className="check-row">
            <label>
              <input
                type="checkbox"
                checked={emailDraft.admin_order_emails_enabled}
                onChange={(event) =>
                  setEmailDraft({
                    ...emailDraft,
                    admin_order_emails_enabled: event.target.checked,
                  })
                }
              />
              Email admin when a new order is placed
            </label>
          </div>
          <label>
            Admin order email
            <input
              type="email"
              placeholder="care@dentixuae.com"
              value={emailDraft.admin_order_email}
              onChange={(event) =>
                setEmailDraft({
                  ...emailDraft,
                  admin_order_email: event.target.value,
                })
              }
            />
          </label>
          <div className="check-row">
            <label>
              <input
                type="checkbox"
                checked={emailDraft.customer_order_emails_enabled}
                onChange={(event) =>
                  setEmailDraft({
                    ...emailDraft,
                    customer_order_emails_enabled: event.target.checked,
                  })
                }
              />
              Email customers their order confirmation
            </label>
          </div>
          <button className="primary-button" disabled={busy}>
            {busy ? "Saving..." : "Save email notifications"}
          </button>
        </div>
      </form>
      <form className="panel" onSubmit={saveShipping}>
        <PanelHeader title="Delivery" subtitle="UAE checkout rules" />
        <div className="settings-form">
          <label>
            Free delivery threshold
            <input
              type="number"
              min="0"
              step="0.01"
              value={shippingDraft.free_shipping_threshold_aed}
              onChange={(event) =>
                setShippingDraft({
                  ...shippingDraft,
                  free_shipping_threshold_aed: Number(event.target.value),
                })
              }
            />
          </label>
          <label>
            Delivery fee
            <input
              type="number"
              min="0"
              step="0.01"
              value={shippingDraft.delivery_fee_aed}
              onChange={(event) =>
                setShippingDraft({
                  ...shippingDraft,
                  delivery_fee_aed: Number(event.target.value),
                })
              }
            />
          </label>
          <div className="settings-row">
            <span>Providers</span>
            <strong>{settings.payments.providers.join(", ")}</strong>
          </div>
          <button className="primary-button" disabled={busy}>
            {busy ? "Saving..." : "Save delivery rules"}
          </button>
        </div>
      </form>
    </section>
  )
}

function AdminUsersScreen({
  users,
  onChanged,
}: {
  users: AdminUser[]
  onChanged: () => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createdPassword, setCreatedPassword] = useState("")

  async function addAdmin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const formEl = event.currentTarget
    const form = new FormData(formEl)
    const password = String(form.get("password") || "")
    setBusy(true)
    setError(null)
    setCreatedPassword("")
    try {
      await createAdminUser({
        email: String(form.get("email") || ""),
        name: String(form.get("name") || ""),
        role: String(form.get("role") || "admin"),
        password,
        enabled: true,
      })
      setCreatedPassword(password)
      formEl.reset()
      await onChanged()
    } catch (err) {
      setError(messageFrom(err))
    } finally {
      setBusy(false)
    }
  }

  async function toggle(user: AdminUser) {
    setBusy(true)
    setError(null)
    try {
      await updateAdminUser(user.id, { enabled: !user.enabled })
      await onChanged()
    } catch (err) {
      setError(messageFrom(err))
    } finally {
      setBusy(false)
    }
  }

  async function remove(user: AdminUser) {
    const confirmed = window.confirm(
      `Delete admin access for ${user.email}? This removes access to the admin panel.`
    )
    if (!confirmed) return

    setBusy(true)
    setError(null)
    try {
      await deleteAdminUser(user.id)
      await onChanged()
    } catch (err) {
      setError(messageFrom(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="split-grid">
      <div className="panel">
        <PanelHeader title="Admin users" subtitle="People allowed into this admin" />
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.name || "Admin"}</td>
                  <td>{user.email}</td>
                  <td>{user.role}</td>
                  <td>
                    <StatusPill value={user.enabled ? "enabled" : "disabled"} />
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        className="secondary-button"
                        disabled={busy}
                        onClick={() => toggle(user)}
                      >
                        {user.enabled ? "Disable" : "Enable"}
                      </button>
                      <button
                        className="danger-button"
                        disabled={busy}
                        onClick={() => remove(user)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!users.length ? (
                <tr>
                  <td colSpan={5}>No admin users yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      <form className="panel details-panel admin-user-form" onSubmit={addAdmin}>
        <PanelHeader title="Add admin" subtitle="Create the admin login and grant access" />
        <label>
          Name
          <input name="name" placeholder="Dentix Admin" />
        </label>
        <label>
          Email
          <input name="email" type="email" required placeholder="admin@example.com" />
        </label>
        <label>
          Password
          <input
            name="password"
            type="text"
            required
            minLength={6}
            autoComplete="new-password"
            placeholder="Create admin password"
          />
        </label>
        <label>
          Role
          <select name="role" defaultValue="admin">
            <option value="admin">admin</option>
            <option value="owner">owner</option>
          </select>
        </label>
        {createdPassword ? (
          <div className="notice">
            Admin created. Password: <strong>{createdPassword}</strong>
          </div>
        ) : null}
        {error ? <div className="notice danger">{error}</div> : null}
        <button className="primary-button" disabled={busy}>
          {busy ? "Saving..." : "Add admin"}
        </button>
      </form>
    </section>
  )
}

function CouponsScreen({
  coupons,
  onChanged,
}: {
  coupons: AdminCoupon[]
  onChanged: () => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [type, setType] = useState<AdminCoupon["type"]>("fixed")

  async function addCoupon(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const form = new FormData(event.currentTarget)
    try {
      await createCoupon({
        code: String(form.get("code") || ""),
        type,
        amount_aed: Number(form.get("amount_aed") || 0),
        percentage: Number(form.get("percentage") || 0),
        minimum_subtotal_aed: form.get("minimum_subtotal_aed")
          ? Number(form.get("minimum_subtotal_aed"))
          : null,
        usage_limit: form.get("usage_limit")
          ? Number(form.get("usage_limit"))
          : null,
        enabled: true,
      })
      event.currentTarget.reset()
      setType("fixed")
      await onChanged()
    } catch (err) {
      setError(messageFrom(err))
    } finally {
      setBusy(false)
    }
  }

  async function toggle(coupon: AdminCoupon) {
    setBusy(true)
    setError(null)
    try {
      await updateCoupon(coupon.id, { enabled: !coupon.enabled })
      await onChanged()
    } catch (err) {
      setError(messageFrom(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="split-grid">
      <div className="panel">
        <PanelHeader title="Coupons" subtitle="Codes customers can apply at checkout" />
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Minimum</th>
                <th>Used</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {coupons.map((coupon) => (
                <tr key={coupon.id}>
                  <td>{coupon.code}</td>
                  <td>
                    {coupon.type === "percentage"
                      ? `${coupon.percentage || 0}%`
                      : formatMoney(coupon.amount_aed)}
                  </td>
                  <td>
                    {coupon.minimum_subtotal_aed
                      ? formatMoney(coupon.minimum_subtotal_aed)
                      : "-"}
                  </td>
                  <td>
                    {coupon.used_count}
                    {coupon.usage_limit ? `/${coupon.usage_limit}` : ""}
                  </td>
                  <td>
                    <StatusPill value={coupon.enabled ? "enabled" : "disabled"} />
                  </td>
                  <td>
                    <button
                      className="secondary-button"
                      disabled={busy}
                      onClick={() => toggle(coupon)}
                    >
                      {coupon.enabled ? "Disable" : "Enable"}
                    </button>
                  </td>
                </tr>
              ))}
              {!coupons.length ? (
                <tr>
                  <td colSpan={6}>No coupons yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      <form className="panel details-panel admin-user-form" onSubmit={addCoupon}>
        <PanelHeader title="Add coupon" subtitle="Create a checkout discount code" />
        <label>
          Code
          <input name="code" required placeholder="WELCOME10" />
        </label>
        <label>
          Type
          <select
            name="type"
            value={type}
            onChange={(event) => setType(event.target.value as AdminCoupon["type"])}
          >
            <option value="fixed">fixed</option>
            <option value="percentage">percentage</option>
          </select>
        </label>
        {type === "fixed" ? (
          <label>
            Amount AED
            <input name="amount_aed" type="number" min="0" step="0.01" required />
          </label>
        ) : (
          <label>
            Percentage
            <input name="percentage" type="number" min="1" max="100" step="0.01" required />
          </label>
        )}
        <label>
          Minimum subtotal AED
          <input name="minimum_subtotal_aed" type="number" min="0" step="0.01" />
        </label>
        <label>
          Usage limit
          <input name="usage_limit" type="number" min="1" step="1" />
        </label>
        {error ? <div className="notice danger">{error}</div> : null}
        <button className="primary-button" disabled={busy}>
          {busy ? "Saving..." : "Add coupon"}
        </button>
      </form>
    </section>
  )
}

function AddressCard({
  title,
  address,
}: {
  title: string
  address?: Record<string, string>
}) {
  const lines = address
    ? [
        [address.first_name, address.last_name].filter(Boolean).join(" "),
        address.company,
        address.address_1,
        address.address_2,
        [address.city, address.province, address.postal_code].filter(Boolean).join(", "),
        address.phone,
      ].filter(Boolean)
    : []

  return (
    <div className="address-card">
      <strong>{title}</strong>
      {lines.length ? (
        lines.map((line) => <span key={line}>{line}</span>)
      ) : (
        <span>No address saved</span>
      )}
    </div>
  )
}

function PanelHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="panel-header">
      <div>
        <h2>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
    </div>
  )
}

function StatusPill({ value, subtle }: { value: string; subtle?: boolean }) {
  return (
    <span className={`status-pill ${subtle ? "subtle" : ""} ${value}`}>
      {value.replace(/_/g, " ")}
    </span>
  )
}

function updateDraftVariant(
  draft: AdminProduct,
  setDraft: (product: AdminProduct) => void,
  patch: Partial<AdminProduct["variants"][number]>
) {
  const variants = draft.variants.length
    ? draft.variants.map((variant, index) =>
        index === 0 ? { ...variant, ...patch } : variant
      )
    : []
  setDraft({
    ...draft,
    variants,
    price_aed:
      typeof patch.price_aed === "number" ? patch.price_aed : draft.price_aed,
    inventory_quantity:
      typeof patch.inventory_quantity === "number"
        ? patch.inventory_quantity
        : draft.inventory_quantity,
  })
}

function getProductSeoAudit(product: AdminProduct) {
  const title = product.title.trim()
  const seoTitle = (product.seo_title || "").trim()
  const seoDescription = (product.seo_description || "").trim()
  const description = (product.description || product.short_description || "").trim()
  const images = product.images || []
  const hasPrice = Number(product.price_aed || product.variants[0]?.price_aed || 0) > 0
  const hasStock = Number(product.inventory_quantity || 0) > 0
  const hasCategory = Boolean(product.categories?.length)
  const hasKeyword =
    title &&
    [seoTitle, seoDescription, product.handle, description]
      .join(" ")
      .toLowerCase()
      .includes(title.split(/\s+/)[0].toLowerCase())

  const checks = [
    { label: "Main image", ok: Boolean(product.thumbnail || images[0]) },
    { label: "Gallery images", ok: images.length >= 2 },
    { label: "SEO title 35-60 chars", ok: seoTitle.length >= 35 && seoTitle.length <= 60 },
    {
      label: "Meta description 120-160 chars",
      ok: seoDescription.length >= 120 && seoDescription.length <= 160,
    },
    { label: "Product description", ok: description.length >= 80 },
    { label: "Clean URL handle", ok: /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(product.handle) },
    { label: "Brand", ok: Boolean(product.brand) },
    { label: "Category", ok: hasCategory },
    { label: "Price", ok: hasPrice },
    { label: "Stock", ok: hasStock },
    { label: "Keyword match", ok: Boolean(hasKeyword) },
    { label: "Google category", ok: Boolean(product.google_product_category) },
  ]

  const score = Math.round(
    (checks.filter((check) => check.ok).length / checks.length) * 100
  )
  const status =
    score >= 90
      ? "Excellent product SEO"
      : score >= 75
        ? "Good, improve a few fields"
        : score >= 50
          ? "Needs SEO work"
          : "Missing important SEO fields"

  return { score, status, checks }
}

function titleFor(screen: Screen) {
  switch (screen) {
    case "orders":
      return "Orders"
    case "products":
      return "Products"
    case "customers":
      return "Customers"
    case "returns":
      return "Returns & Refunds"
    case "inventory":
      return "Inventory"
    case "settings":
      return "Settings"
    case "admins":
      return "Admin Users"
    case "coupons":
      return "Coupons"
    default:
      return "Dashboard"
  }
}

function stockLabel(quantity: number) {
  if (quantity <= 0) return "out_of_stock"
  if (quantity <= 5) return "low_stock"
  return "in_stock"
}

function initials(value: string) {
  return value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("")
}

function stringValue(value: unknown) {
  return typeof value === "string" ? value : ""
}

function formatMoney(value: number) {
  return new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency: "AED",
    maximumFractionDigits: 2,
  }).format(Number(value || 0))
}

function formatDate(value?: string) {
  if (!value) return "-"
  return new Intl.DateTimeFormat("en-AE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value))
}

function messageFrom(error: unknown) {
  return error instanceof Error ? error.message : String(error)
}
